import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wind, Heart, Zap, Brain, Battery, Sparkles } from 'lucide-react';
import { useFullscreen } from '@/contexts/FullscreenContext';

/**
 * JourneyRespirationDiscovery - Découverte des 6 Techniques de Respiration Hearen
 * Écran pédagogique avec carrousel/grille des exercices
 */
const JourneyRespirationDiscovery = () => {
  const navigate = useNavigate();
  const { setIsFullscreen } = useFullscreen();
  const [showContent, setShowContent] = useState(false);
  const [activeExercise, setActiveExercise] = useState(0);
  
  const isEnglish = localStorage.getItem('i18nextLng')?.includes('en') ||
                    localStorage.getItem('hearen_lang') === 'en';

  const exercises = [
    {
      id: 'coherence',
      icon: Heart,
      name: isEnglish ? 'Cardiac Coherence' : 'Cohérence Cardiaque',
      description: isEnglish ? '5-5 breathing for balance' : 'Respiration 5-5 pour l\'équilibre',
      color: 'rgba(239, 68, 68, 0.8)',
      pattern: '5s - 5s'
    },
    {
      id: 'square',
      icon: Wind,
      name: isEnglish ? 'Square Breathing' : 'Respiration Carrée',
      description: isEnglish ? '4-4-4-4 for calm' : '4-4-4-4 pour le calme',
      color: 'rgba(59, 130, 246, 0.8)',
      pattern: '4-4-4-4'
    },
    {
      id: '478',
      icon: Brain,
      name: isEnglish ? '4-7-8' : '4-7-8',
      description: isEnglish ? 'Deep relaxation' : 'Relaxation profonde',
      color: 'rgba(168, 85, 247, 0.8)',
      pattern: '4-7-8'
    },
    {
      id: 'antistress',
      icon: Sparkles,
      name: isEnglish ? 'Anti-Stress' : 'Anti-stress',
      description: isEnglish ? 'Instant relief' : 'Soulagement instantané',
      color: 'rgba(16, 185, 129, 0.8)',
      pattern: '4-6'
    },
    {
      id: 'energizing',
      icon: Zap,
      name: isEnglish ? 'Energizing' : 'Énergétique',
      description: isEnglish ? 'Vitality boost' : 'Boost de vitalité',
      color: 'rgba(234, 179, 8, 0.8)',
      pattern: '3-3'
    },
    {
      id: 'slow',
      icon: Battery,
      name: isEnglish ? 'Slow' : 'Lente',
      description: isEnglish ? 'Deep rest' : 'Repos profond',
      color: 'rgba(139, 92, 246, 0.8)',
      pattern: '6-6'
    }
  ];

  useEffect(() => {
    setIsFullscreen(true);
    
    // Fade-in progressif
    setTimeout(() => setShowContent(true), 300);
    
    // Carrousel automatique
    const interval = setInterval(() => {
      setActiveExercise(prev => (prev + 1) % exercises.length);
    }, 3000);
    
    // Note: Le son Ambient 3 joue déjà en continu depuis JourneyIntro
    console.log('🎵 [JourneyRespirationDiscovery] Ambient music already playing from Journey start');
    
    // Cleanup fonction
    return () => {
      clearInterval(interval);
      setIsFullscreen(false);
    };
  }, [setIsFullscreen]);

  const handleContinue = () => {
    console.log('💨 JourneyRespirationDiscovery: Navigating to /journey-audio-test');
    // L'audio continue - pas d'arrêt ici
    navigate('/journey-audio-test');
  };

  const currentExercise = exercises[activeExercise];
  const Icon = currentExercise.icon;

  return (
    <div className="min-h-screen bg-[#0A0F1C] pb-32 relative overflow-hidden flex flex-col items-center justify-center px-6">
      {/* Background gradient */}
      <div 
        className="absolute inset-0 opacity-20"
        style={{
          background: `radial-gradient(circle at 50% 50%, ${currentExercise.color} 0%, transparent 70%)`,
          transition: 'all 1s ease-in-out',
          pointerEvents: 'none',
        }}
      />

      {/* Contenu principal - Fade-in + Translation */}
      <div
        className="relative z-10 max-w-md w-full transition-all duration-700 ease-out"
        style={{
          opacity: showContent ? 1 : 0,
          transform: showContent ? 'translateY(0)' : 'translateY(30px)',
        }}
      >
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-3">
            {isEnglish ? 'Discover Hearen Breathing' : 'Découvrez la Respiration Hearen'}
          </h1>
          <p className="text-white/70 text-sm">
            {isEnglish 
              ? '6 techniques to soothe your nervous system' 
              : '6 techniques pour apaiser votre système nerveux'
            }
          </p>
        </div>

        {/* Aperçu Cohérence Cardiaque Premium (Exercice Actif) */}
        <div
          className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 mb-6"
          style={{
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            transition: 'all 1s ease-in-out',
          }}
        >
          {/* Icône animée */}
          <div className="flex justify-center mb-6">
            <div
              className="w-24 h-24 rounded-full flex items-center justify-center"
              style={{
                background: currentExercise.color,
                boxShadow: `0 0 40px ${currentExercise.color}`,
                animation: 'icon-pulse 2s ease-in-out infinite',
                transition: 'all 1s ease-in-out',
              }}
            >
              <Icon className="w-12 h-12 text-white" />
            </div>
          </div>

          {/* Info exercice */}
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-white mb-2">
              {currentExercise.name}
            </h2>
            <p className="text-white/70 text-sm mb-3">
              {currentExercise.description}
            </p>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10">
              <span className="text-white/90 text-sm font-semibold">
                {currentExercise.pattern}
              </span>
            </div>
          </div>

          {/* Orbe animée (aperçu) */}
          <div className="flex justify-center">
            <div
              className="w-32 h-32 rounded-full"
              style={{
                background: `radial-gradient(circle, ${currentExercise.color} 0%, transparent 70%)`,
                filter: 'blur(20px)',
                animation: 'orb-breathe 4s ease-in-out infinite',
              }}
            />
          </div>
        </div>

        {/* Indicateurs de carrousel */}
        <div className="flex justify-center gap-2 mb-8">
          {exercises.map((_, index) => (
            <div
              key={index}
              className="transition-all duration-300"
              style={{
                width: index === activeExercise ? '24px' : '8px',
                height: '8px',
                borderRadius: '4px',
                background: index === activeExercise 
                  ? currentExercise.color 
                  : 'rgba(255, 255, 255, 0.2)',
              }}
            />
          ))}
        </div>

        {/* Grille des 6 techniques (aperçu compact) */}
        <div className="grid grid-cols-3 gap-3 mb-8">
          {exercises.map((exercise, index) => {
            const ExerciseIcon = exercise.icon;
            return (
              <div
                key={exercise.id}
                className="px-3 py-4 rounded-xl text-center transition-all duration-300"
                style={{
                  background: index === activeExercise 
                    ? 'rgba(255, 255, 255, 0.1)' 
                    : 'rgba(255, 255, 255, 0.05)',
                  border: `1px solid ${index === activeExercise ? exercise.color : 'rgba(255, 255, 255, 0.1)'}`,
                }}
              >
                <ExerciseIcon 
                  className="w-6 h-6 mx-auto mb-2" 
                  style={{ 
                    color: index === activeExercise ? exercise.color : 'rgba(255, 255, 255, 0.5)' 
                  }} 
                />
                <p className="text-white/70 text-xs">
                  {exercise.name.split(' ')[0]}
                </p>
              </div>
            );
          })}
        </div>

        {/* CTA Continuer */}
        <div className="flex justify-center">
          <button
            onClick={handleContinue}
            className="px-8 py-4 rounded-full font-semibold text-lg transition-all hover:scale-105 active:scale-95 text-white"
            style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              boxShadow: '0 8px 30px rgba(16, 185, 129, 0.4)',
            }}
          >
            {isEnglish ? 'Continue' : 'Continuer'}
          </button>
        </div>
      </div>

      {/* Animations CSS */}
      <style>{`
        @keyframes icon-pulse {
          0%, 100% { 
            transform: scale(1); 
          }
          50% { 
            transform: scale(1.1); 
          }
        }
        
        @keyframes orb-breathe {
          0%, 100% { 
            transform: scale(1); 
            opacity: 0.6; 
          }
          50% { 
            transform: scale(1.3); 
            opacity: 0.9; 
          }
        }
      `}</style>
    </div>
  );
};

export default JourneyRespirationDiscovery;
